import  React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import { accountActions, organizationActions, periodsActions } from '../../redux/actions/';
import { accountServices } from '../../redux/services';
import { Layout, Tabs, Typography, Button, Modal, Col, Row } from 'antd';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import AccountStructureComponent from '../../components/AccountTable';
import FavoriteAccount from '../../components/FavoriteAccount';
import AccountManagement from '../../components/AccountManagement';
import TabNameModal from '../../components/TabNameModal';
import OverlayedLoader from '../../components/OverlayedLoader';
import RenderUpdater from '../../components/RenderUpdater';
import UndoButton from '../../components/UndoButton';
import Localization from '../../utils/localization';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import TabContent from '../../components/TabContent';
import { useAccountModals } from '../../hooks/useAccountModals';
import { useAccountLocator } from '../../hooks/useAccountLocator';
import AccountSearch from '../../components/AccountSearch';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { NUM_DEFAULT_TABS, customTabKey, getCustomTabs, findCustomTabByKey, isCustomTabKey } from '../../utils/accountStructure/customTabs';

const { TabPane } = Tabs;
const { Title } = Typography;
const { Content } = Layout;

const DEFAULT_WIDTHS = { account_number: 100, name: 170 };

const nodeKeyOf = (node) =>
    node.props?.['data-node-key'] ??
    String(node.key ?? '').replace(/^\.\$/, '');

const DraggableTabNode = ({ nodeKey, children }) => {
    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: nodeKey });

    return React.cloneElement(children, {
        ref: setNodeRef,
        style: {
            ...children.props.style,
            transform: CSS.Translate.toString(transform),
            transition,
            cursor: 'grab',
            opacity: isDragging ? 0.6 : 1,
        },
        ...attributes,
        ...listeners,
    });
};


const AccountStructure = () => {
    const [columnWidths, setColumnWidth] = useColumnWidths(
    'accountTable.columnWidths',
    DEFAULT_WIDTHS
);
    const dispatch = useDispatch();
    const accountStructure = useSelector((state) => state.accounts.accountStructure);
    const organization = useSelector((state) => state.organization.organization);
    const periods = useSelector((state) => state.periods.periods);
    const expandedPeriods = useSelector((state) => state.periods.expandedPeriods);

    const fetchingStructure = useSelector((state) => state.accounts.fetchingStructure);
    const fetchingExpandedPeriods = useSelector((state) => state.periods.fetchingExpandedPeriods);
    const creatingAccount = useSelector((state) => state.accounts.creatingAccount);
    const creatingSubAccount = useSelector((state) => state.accounts.creatingSubAccount);
    const updatingAccount = useSelector((state) => state.accounts.updatingAccount);
    const deletingAccount = useSelector((state) => state.accounts.deletingAccount);
    const deletingSeparator = useSelector((state) => state.accounts.deletingSeparator);
    const updatingSeparator = useSelector((state) => state.accounts.updatingSeparator);
    const insertingAccount = useSelector((state) => state.accounts.insertingAccount);
    const movingAccountUp = useSelector((state) => state.accounts.movingAccountUp);
    const movingAccountDown = useSelector((state) => state.accounts.movingAccountDown);
    const movingAccountLeft = useSelector((state) => state.accounts.movingAccountLeft);
    const movingAccountRight = useSelector((state) => state.accounts.movingAccountRight);
    const creatingCustomTab = useSelector((state) => state.accounts.creatingCustomTab);
    const deletingCustomTab = useSelector((state) => state.accounts.deletingCustomTab);
    const updatingCustomTabAccount = useSelector((state) => state.accounts.updatingCustomTabAccount);
    const movingCustomTabAccountUp = useSelector((state) => state.accounts.movingCustomTabAccountUp);
    const movingCustomTabAccountDown = useSelector((state) => state.accounts.movingCustomTabAccountDown);
    const deletingCustomTabAccount = useSelector((state) => state.accounts.deletingCustomTabAccount);
    const updatingOrganization = useSelector((state) => state.organization.updatingOrganization);

    const [activeKey, setActiveKey] = useState('1');
    const [tabNameInputOpen, updateCustomTabInputOpen] = useState(false);
    const [addFavoriteOpen, setAddFavoriteOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [isAccountManagementOpen, setIsAccountManagementOpen] = useState(false);
    const [selectedRowKey, setSelectedRowKey] = useState([]);
   

    const bsAssetsRef = useRef(null);
    const bsLiabRef = useRef(null);
    const bsEquityRef = useRef(null);
    
    useEffect(() => {
        dispatch(accountActions.getAccountStructure());
        dispatch(organizationActions.getOrganization());
        dispatch(periodsActions.getPeriods());
    }, [dispatch]);

    useEffect(() => {
        if (expandedPeriods == null) {
            if (!fetchingExpandedPeriods) dispatch(periodsActions.getExpandedPeriods());
        }
    }, [dispatch, fetchingExpandedPeriods, expandedPeriods]);

    useEffect(() => {
        if (selectedAccount != null && selectedAccount.account == null) {
            const updatedSelectedAccount = { copy: '' };
            if (selectedAccount.associated_account_id == null) {
                accountServices.findAccount(updatedSelectedAccount, selectedAccount.account_id, accountStructure);
            } else if (selectedAccount.associated_account_id != null) {
                accountServices.findAccount(
                    updatedSelectedAccount,
                    selectedAccount.associated_account_id,
                    accountStructure,
                );
            }

            setSelectedAccount(updatedSelectedAccount.copy);
        }
    }, [setSelectedAccount, selectedAccount, accountStructure]);

    useEffect(() => {
      
      if (activeKey !== "2") return;
      
      let handlers = [];
      let timeoutId;
      
      const bind = () => {
        const getScroller = (selector) =>
          document.querySelector(`${selector} .ant-table-body`) ||
        document.querySelector(`${selector} .ant-table-content`);
        
        const els = [
          getScroller(".bs-assets-table"),
          getScroller(".bs-liab-table"),
          getScroller(".bs-equity-table"),
        ].filter(Boolean);
        
        if (els.length < 2) return;
        
        let isSyncing = false;
        
        handlers = els.map((el) => {
          const handler = () => {
            if (isSyncing) return;
            
            isSyncing = true;
            const left = el.scrollLeft;
            
            els.forEach((other) => {
              if (other !== el) {
                other.scrollLeft = left;
              }
            });
            
            requestAnimationFrame(() => {
              isSyncing = false;
            });
          };
          
          el.addEventListener("scroll", handler, { passive: true });
          return { el, handler };
        });
      };
      
      timeoutId = window.setTimeout(bind, 100);
     
      return () => {
        window.clearTimeout(timeoutId);
        
        handlers.forEach(({ el, handler }) => {
          el.removeEventListener("scroll", handler);
        });
        
        handlers = [];
      };
    }, [activeKey, periods, expandedPeriods]);

    const customTabs = useMemo(() => getCustomTabs(accountStructure), [accountStructure]);
    const customKeys = useMemo(() => customTabs.map(customTabKey), [customTabs]);
    const customKeySet = useMemo(() => new Set(customKeys), [customKeys]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    );

    // Drop stale numeric custom-tab keys from before stable keys landed.
    useEffect(() => {
        if (Number(activeKey) > NUM_DEFAULT_TABS) setActiveKey('1');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The active tab can disappear (deleted here or in another session).
    useEffect(() => {
        if (!isCustomTabKey(activeKey)) return;
        if (!findCustomTabByKey(accountStructure, activeKey)) setActiveKey('1');
    }, [accountStructure, activeKey]);

    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        if (!customKeySet.has(active.id) || !customKeySet.has(over.id)) return;

        const next = arrayMove(
            customTabs,
            customKeys.indexOf(active.id),
            customKeys.indexOf(over.id),
        );

        dispatch(accountActions.reorderCustomTabs(
            next.map((t) => t.financial_group_type),
        ));
    };

    const renderTabBar = (tabBarProps, DefaultTabBar) => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={onDragEnd}
        >
            <SortableContext items={customKeys} strategy={horizontalListSortingStrategy}>
                <DefaultTabBar {...tabBarProps}>
                    {(node) => {
                        const nodeKey = nodeKeyOf(node);
                        return customKeySet.has(nodeKey) ? (
                            <DraggableTabNode nodeKey={nodeKey} key={node.key}>
                                {node}
                            </DraggableTabNode>
                        ) : node;
                    }}
                </DefaultTabBar>
            </SortableContext>
        </DndContext>
    );

    const handleSetExpandedPeriods = (expandedPeriods) => {
        dispatch(periodsActions.setExpandedPeriods(expandedPeriods));
    };

    const handleCreateAccount = (ftype) => {
        if (!creatingAccount) dispatch(accountActions.createAccount(ftype, accountStructure));
    };

    const createSubInFlightRef = useRef(false);

    const handleCreateSubAccount = useCallback((account, ftype) => {
          if (!account?.account_id) return;
          if (createSubInFlightRef.current) return;
          createSubInFlightRef.current = true;

          Promise.resolve(
              dispatch(accountActions.createSubAccount(account, ftype))
          ).finally(() => {
              createSubInFlightRef.current = false;
          });
      }, [dispatch]);

    const handleUpdateAccount = (oldAccount, account) => {
        if (!updatingAccount) dispatch(accountActions.updateAccount(oldAccount, account));
    };

    const handleDeleteAccount = (account) => {
        if (!deletingAccount) dispatch(accountActions.deleteAccount(account));
    };

    const handleInsertAccount = (e, account) => {
        e.stopPropagation();
        if (!insertingAccount) dispatch(accountActions.insertAccount(account));
    };

    const handleMoveAccountUp = (e, account) => {
    e.stopPropagation();

    if (!movingAccountUp) {
        dispatch(accountActions.moveAccountUp(account));
    }
};
    const handleMoveAccountDown = (e, account) => {
    e.stopPropagation();

    if (!movingAccountDown) {
        dispatch(accountActions.moveAccountDown(account));
    }
};

    const handleMoveAccountLeft = (e, account) => {
        e.stopPropagation();

        if (!movingAccountLeft) 
          dispatch(accountActions.moveAccountLeft(account));
};

    const handleMoveAccountRight = (e, account) => {
        e.stopPropagation();
        if (!movingAccountRight) dispatch(accountActions.moveAccountRight(account));
    };

    const handleMoveSeparator = (separator, direction) => {
        if (
            separator?.separator_id == null ||
            !['up', 'down'].includes(direction)
        ) {
            return;
        }

        dispatch(
            accountActions.moveSeparator(
                separator.separator_id,
                direction,
            ),
        );
    };

    const handleCreateCustomTab = () => {
        if (!creatingCustomTab) dispatch(accountActions.createCustomTab('Custom Tab'));
    };

    const handleUpdateCustomAccount = (account) => {
        if (!updatingCustomTabAccount) dispatch(accountActions.updateCustomTabAccount(account));
    };

    const handleMoveCustomAccountUp = (e, account) => {
        e.stopPropagation();
        if (!movingCustomTabAccountUp) dispatch(accountActions.moveCustomTabAccountUp(account));
    };

    const handleMoveCustomAccountDown = (e, account) => {
        e.stopPropagation();
        if (!movingCustomTabAccountDown) dispatch(accountActions.moveCustomTabAccountDown(account));
    };

    const handleDeleteCustomTabAccount = (account) => {
        if (!deletingCustomTabAccount) dispatch(accountActions.deleteCustomTabAccount(account));
    };

    const { focusRequest, locateAccount, clearLocate } = useAccountLocator({
      accountStructure,
      activeKey,
      setActiveKey,
    });

const {
      callbacks: menuCallbacks,
      modals: accountModals,
    } = useAccountModals({
      accountStructure,
      handleInsertAccount,
      handleCreateSubAccount,
      handleDeleteAccount,
      handleDeleteCustomTabAccount,
      onLocateAccount: locateAccount,
    });

    const handleRemoveCustomTab = (targetKey) => {
        const tab = findCustomTabByKey(accountStructure, targetKey);
        if (!tab) return;

        Modal.confirm({
            title: 'Delete Tab?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone',
            okText: 'OK',
            cancelText: 'Cancel',
            onOk: () => {
                if (!deletingCustomTab) {
                    dispatch(accountActions.deleteCustomTab(
                        tab.financial_group_type,
                        accountStructure,
                    ));
                }
            },
        });
    };

  const handleCreateSeparator = (payload) => {
    console.log('AccountStructure received separator:', payload);

      dispatch(accountActions.createSeparator(payload));
  };

  const handleUpdateSeparator = (
    separator,
    separatorType,
    separatorText = null,
  ) => {
    if (
      updatingSeparator ||
      separator?.separator_id == null ||
      !separatorType
    ) {
      return;
    }

     const text =
        separatorType === 'text'
          ? String(
              separatorText ??
                separator.separator_text ??
                '',
            ).trim()
          : null;

      if (separatorType === 'text' && !text) {
        return;
      }

    dispatch(
      accountActions.updateSeparator(
        separator.separator_id,
        {
          separatorType,
          separatorText: text,
        },
      ),
    );
  };

  const handleDeleteSeparator = (separator) => {
     if (
        deletingSeparator ||
        separator?.separator_id == null
    ) {
        return;
    }

    dispatch(
        accountActions.deleteSeparator(
            separator.separator_id,
        ),
    );
};

    const handleSetSelectedAccount = (selectedAccount) => {
        setSelectedAccount(selectedAccount);
      };

      const openAccountManagement = (account) => {
          
      if (!account) return;
      setSelectedAccount(account);
      setIsAccountManagementOpen(true);
    };

    const getFinancialGroupTypes = () => {
        let count = NUM_DEFAULT_TABS + 1;
        let newFinancialGroupTypes = {};

        if (accountStructure) {
            let customs = accountStructure?.custom.flat();

            for (let i = 0; i < customs.length; i++) {
                newFinancialGroupTypes[count] = customs[i].financial_group_type;
                count++;
            }
        }
        return newFinancialGroupTypes;
    };

    const onChange = (newActiveKey) => {
        setActiveKey(newActiveKey);
    };

    const onEdit = (targetKey, action) => {
        if (action === 'add') {
            handleCreateCustomTab();
        } else {
            handleRemoveCustomTab(targetKey);
        }
    };

const renderContent = () => {
  if (fetchingStructure) return <OverlayedLoader />;
  if (accountStructure === undefined || periods === null) return <OverlayedLoader />;
  if (updatingOrganization) return <RenderUpdater />;

  // Bundle all the props that every tab needs
  const handlers = {
    handleUpdateAccount,
    handleCreateSubAccount,
    handleDeleteAccount,
    handleDeleteCustomTabAccount,
    handleMoveAccountUp,
    handleMoveAccountDown,
    handleMoveAccountLeft,
    handleMoveAccountRight,
    handleInsertAccount,
    handleSetSelectedAccount,
    handleSetExpandedPeriods,

    handleCreateSeparator,
    handleUpdateSeparator,
    handleDeleteSeparator,
    handleMoveSeparator,

     // ...menuCallbacks,
  };

  

  const sharedProps = {
    accountStructure,
    organization,
    periods,
    expandedPeriods,
    selectedRowKey,
    setSelectedRowKey,
    selectedAccount,
    isAccountManagementOpen,
    setIsAccountManagementOpen,
    columnWidths,
    setColumnWidth,
    handlers,

    ...menuCallbacks,

    onOpenAccountManagement: openAccountManagement,
    focusRequest,
};

  let count = 0;

  return (
  

      <Tabs 
        onChange={onChange} 
        activeKey={activeKey} 
        type="editable-card" 
        onEdit={onEdit} 
        renderTabBar={renderTabBar}
        tabBarExtraContent={
          <>
            <AccountSearch
              accountStructure={accountStructure}
              value={focusRequest?.identity}
              onLocate={locateAccount}
              onClear={clearLocate}
              onShowWhereUsed={menuCallbacks.onShowWhereUsed}
              style={{ marginRight: '16px' }}
            />
            <UndoButton style={{ marginRight: '16px' }} />
          </>
        }
        destroyInactiveTabPane>

        <TabPane closable={false} tab={Localization.common.profitAndLoss} key="1">
          <TabContent
            {...sharedProps}
            accounts={accountStructure?.profitloss}
            ftype="PL"
            onAddAccount={() => handleCreateAccount('PL')}
          />
        </TabPane>

        <TabPane closable={false} tab={Localization.common.balanceSheet} key="2">
        <div className="bs-assets-table">
          <TabContent
            {...sharedProps}
            accounts={accountStructure?.balancesheet?.assets}
            ftype="BSA"
            title="Assets"
            wrapperRef={bsAssetsRef}
            onAddAccount={() => handleCreateAccount('BSA')}
          />
          </div>
          <div className="bs-liab-table">
          <TabContent
            {...sharedProps}
            accounts={accountStructure?.balancesheet?.liabilities}
            ftype="BSL"
            title={Localization.common.liabilities}
            wrapperRef={bsLiabRef}
            onAddAccount={() => handleCreateAccount('BSL')}
          />
          </div>
          <div className="bs-equity-table">
          <TabContent
            {...sharedProps}
            accounts={accountStructure?.balancesheet?.equities}
            ftype="BSE"
            title={Localization.common.equities}
            wrapperRef={bsEquityRef}
            onAddAccount={() => handleCreateAccount('BSE')}
          />
          </div>
        </TabPane>

        <TabPane closable={false} tab={Localization.common.cashFlow} key="3">
          <TabContent
            {...sharedProps}
            accounts={accountStructure?.cashflow}
            ftype="CF"
            onAddAccount={() => handleCreateAccount('CF')}
          />
        </TabPane>

        <TabPane closable={false} tab={Localization.common.drivers} key="4">
          <TabContent
            {...sharedProps}
            accounts={accountStructure?.drivers}
            ftype="DRVE"
            onAddAccount={() => handleCreateAccount('DRVE')}
          />
        </TabPane>

        {/* Custom tabs kept as-is — different handlers, different shape */}
        {customTabs.map((tab) => {
          const key = customTabKey(tab);

          return (
            <TabPane tab={<span className="custom-tab-label">{tab.name}</span>} key={key}>
              <Title className="accountstructure-h1">
                {tab.name}
                <Button
                  type="text"
                  style={{ marginLeft: '5px', padding: '2px 4px', fontSize: '1.5rem' }}
                  onClick={() => updateCustomTabInputOpen(true)}
                >
                  <EditOutlined />
                </Button>
              </Title>
              <Row>
                <Col span={12}></Col>
                <Col span={12}>
                  <Button
                    type="primary"
                    style={{ float: 'right', marginRight: '40px', marginBottom: '25px' }}
                    onClick={() => {
                        if (!selectedAccount) return;
                        openAccountManagement(selectedAccount);
                      }}
                  >
                    Account Management
                  </Button>
                 
                </Col>
              </Row>
              <AccountStructureComponent
                accounts={tab.custom_tab_accounts}
                accountStructure={accountStructure}
                organization={organization}
                periods={periods}
                expandedPeriods={expandedPeriods}
                selectedRowKey={selectedRowKey}
                setSelectedRowKey={setSelectedRowKey}
                handleUpdateAccount={handleUpdateCustomAccount}
                handleCreateSeparator={handleCreateSeparator}
                handleUpdateSeparator={handleUpdateSeparator}
                handleDeleteSeparator={handleDeleteSeparator}
                handleMoveCustomAccountUp={handleMoveCustomAccountUp}
                handleMoveCustomAccountDown={handleMoveCustomAccountDown}
                handleMoveSeparator={handleMoveSeparator}
                handleCreateSubAccount={handleCreateSubAccount}
                handleDeleteCustomTabAccount={handleDeleteCustomTabAccount}
                handleSetSelectedAccount={handleSetSelectedAccount}
                handleSetExpandedPeriods={handleSetExpandedPeriods}
                columnWidths={columnWidths}
                setColumnWidth={setColumnWidth}
                customTabIndex={+key}
                financialGroupType={tab.financial_group_type}
                onOpenAccountManagement={openAccountManagement}
                focusRequest={focusRequest}

                 {...menuCallbacks}
              />
              <Button
                type="primary"
                style={{ float: 'right', marginRight: '40px', marginTop: '10px' }}
                onClick={() => setAddFavoriteOpen(true)}
              >
                Add account
              </Button>
              <FavoriteAccount
                accountStructure={accountStructure}
                customTabIndex={+key - NUM_DEFAULT_TABS - 1}
                groupId={tab.group_id}
                financialGroupType={tab.financial_group_type}
                show={addFavoriteOpen && activeKey === key}
                handleClose={() => setAddFavoriteOpen(false)}
              />
              <TabNameModal
                open={tabNameInputOpen && activeKey === key}
                currentName={tab.name}
                currentTab={tab}
                handleClose={() => updateCustomTabInputOpen(false)}
              />
            </TabPane>
          );
        })}
      </Tabs>
  );
};
   return (
    <div className="account-structure-page">
        <Layout style={{ height: "100vh", marginLeft: -16, position: "relative", overflowX: "hidden" }}>
             <Content style={{ width: "100%", overflowY: "auto", overflowX: "hidden"  }}>
            {renderContent()}
          </Content>
          <AccountManagement
            account={selectedAccount}
            accountStructure={accountStructure}
            isAccountManagementOpen={isAccountManagementOpen}
            handleInsertAccount={handleInsertAccount}
            handleCreateSubAccount={handleCreateSubAccount}
            handleDeleteAccount={handleDeleteAccount}
            handleDeleteCustomTabAccount={handleDeleteCustomTabAccount}
            handleClose={() => setIsAccountManagementOpen(false)}
          />
          {accountModals}
        </Layout>
    </div>
);

};

export default AccountStructure;
